import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { classNames } from "@/lib/utils";
import {
  FileEnvironment,
  capitalizedFileType,
  deployToEnvironment,
  mutateVersion,
  useFileEnvironments,
} from "@/services/files.service";
import { Environment } from "@/types/app/environment";
import { File } from "@/types/app/file";
import { RadixAppDialog } from "@components/library/AppDialog";
import Button from "@components/library/Button";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { CheckIcon } from "@heroicons/react/solid";
import { Checkbox } from "antd";
import { useMemo, useState } from "react";
import DefaultTag from "../Organizations/DefaultTag";
import { None } from "../Tables/Logs/LogCells";
import { EnvironmentTag } from "@components/library/EnvironmentTag";
import { ToastVariant } from "@components/library/Toast";
import { hlToastApiError } from "@components/library/Toast";
import { getShortId } from "@/lib/string";
import { hlToast } from "@components/library/Toast";
import { Warning } from "@phosphor-icons/react";

interface DeploymentDialogProps {
  version: File | null;
  open: boolean;
  onClose: () => void;
  onDeploy?: () => void;
}

/** Dialog to deploy a specific version. */
export const ActiveDeploymentDeployDialog = ({ version = null, open, onClose, onDeploy }: DeploymentDialogProps) => {
  const { file, mutate: mutateFile } = useMonitoringFile();

  const [selectedEnvironments, setSelectedEnvironments] = useState<Environment[]>([]);
  const { fileEnvironments, mutate: mutateFileEnvironments } = useFileEnvironments(file?.id);

  const environmentsAlreadyDeployedTo = useMemo(
    () => fileEnvironments?.filter((env) => env.file !== null && env.file.version_id === version?.version_id),
    [fileEnvironments, version],
  );

  // Filter out the Environments that are already selected
  const newEnvironments = selectedEnvironments.filter(
    (selectedEnv) => !environmentsAlreadyDeployedTo?.some((deployedEnv) => deployedEnv.id === selectedEnv.id),
  );

  if (!file) {
    return null;
  }

  if (version === null || fileEnvironments === undefined) {
    return null;
  }

  const handleClose = () => {
    setSelectedEnvironments([]);
    onClose();
  };

  const dialogTitle = (
    <>
      <div className="flex gap-24 px-8 pt-8 text-18-24 font-bold text-gray-800 [text-wrap:balance]">
        Deploy {capitalizedFileType(file.type)}{" "}
        {version ? (
          <>
            {/* Absolute hack just to make this bigger without faffing with the component */}
            <div className="inline-flex scale-[1.2]">
              <FileLabelWithPopover
                fileId={file.id}
                versionId={version.version_id}
                size={24}
                displayFile={false}
                tooltipContentProps={{
                  align: "start",
                  side: "bottom",
                }}
              />
            </div>
          </>
        ) : null}
      </div>
    </>
  );

  return (
    <RadixAppDialog
      open={open}
      onClose={handleClose}
      width="medium"
      title={<div className="w-full justify-center">{dialogTitle}</div>}
    >
      <div className="flex w-full flex-col gap-20 p-8">
        <SelectEnvironments
          versionId={version.version_id}
          fileEnvironments={fileEnvironments}
          newEnvironments={newEnvironments}
          environmentsSelected={selectedEnvironments}
          setEnvironmentsSelected={setSelectedEnvironments}
          onDeploy={async () => {
            try {
              // TODO: Consider writing a bulk-deploy endpoint.
              await Promise.all(
                newEnvironments.map((environment) => {
                  return deployToEnvironment({
                    fileId: file.id,
                    environmentId: environment.id,
                    versionId: version.version_id,
                  });
                }),
              );

              onDeploy && onDeploy();
              setSelectedEnvironments([]);
              // Just stuck a mutate here as it's likely being used (e.g. in Editors).
              // Other uses (e.g. in list versions etc) need to handle their own state refreshes
              // with the `onDeploy` callback.
              mutateVersion({ fileId: file.id, versionId: version.version_id });
              mutateFile();
              mutateFileEnvironments();
              handleClose();

              const deployedVersion = version.version_name
                ? version.version_name
                : `#${getShortId(version.version_id)}`;
              hlToast({
                title: `Version deployed`,
                description: (
                  <>
                    <span>Version</span> <span className="font-medium">{deployedVersion}</span> has been deployed to{" "}
                    {getJoinedEnvironmentNames(newEnvironments)}
                  </>
                ),
                variant: ToastVariant.Success,
              });
            } catch (error) {
              hlToastApiError({
                error,
                titleFallback: "Deployment failed",
                descriptionFallback: `Failed to deploy version.`,
              });
            }
          }}
          onClose={handleClose}
        />
      </div>
    </RadixAppDialog>
  );
};

/**
 * Joins the names of the environments into an element with bolded environment names in a comma separated list.
 * @param environments The environments to join.
 * @returns An element with the environment names. (Element because it contains spans for bolding.)
 */
const getJoinedEnvironmentNames = (environments: Environment[]): JSX.Element => {
  const envNames = environments.map((env, index) => (
    <>
      <span key={env.id} className="font-medium">
        {env.name}
      </span>
      {index < environments.length - 2 && ", "}
    </>
  ));

  if (environments.length === 0) {
    return <></>;
  } else if (environments.length === 1) {
    return envNames[0];
  } else {
    return (
      <>
        {envNames.slice(0, -2)} {envNames[environments.length - 2]} and {envNames[environments.length - 1]}
      </>
    );
  }
};

interface SelectEnvironmentsProps {
  versionId: string;
  fileEnvironments: FileEnvironment[];
  environmentsSelected: Environment[];
  newEnvironments: Environment[];
  setEnvironmentsSelected: (environments: Environment[]) => void;
  onDeploy: () => void;
  onClose: () => void;
}

/* A scrollable list of environments that the user can select to deploy to for a specific version. */
export const SelectEnvironments = ({
  versionId,
  fileEnvironments,
  environmentsSelected,
  setEnvironmentsSelected,
  newEnvironments,
  onDeploy,
  onClose,
}: SelectEnvironmentsProps) => {
  const handleCheckBoxChange = (environmentsSelected: Environment[], environment: Environment) => {
    // If the environment is already selected, remove it from the list. Otherwise, add it.
    const newEnvironmentsSelected = environmentsSelected.includes(environment)
      ? environmentsSelected.filter((selectedEnvironment) => selectedEnvironment.id !== environment.id)
      : [...environmentsSelected, environment];

    setEnvironmentsSelected(newEnvironmentsSelected);
  };

  const handleSelectAllCheckBoxChange = () => {
    setEnvironmentsSelected(environmentsSelected.length === fileEnvironments.length ? [] : fileEnvironments);
  };

  return (
    <div className="flex w-full flex-col justify-between gap-20">
      <div className="flex max-h-[270px] flex-col overflow-y-auto rounded border border-oldgray-400 text-13-16">
        <div className="flex h-48 w-full shrink-0 items-center border-b border-oldgray-300">
          <div className="flex w-1/2 min-w-0 items-center gap-6">
            <div className="flex select-none items-baseline justify-start gap-6 px-16 font-bold text-oldgray-900">
              <Checkbox
                tabIndex={-1}
                indeterminate={environmentsSelected.length > 0 && environmentsSelected.length < fileEnvironments.length}
                name="selectAll"
                checked={environmentsSelected.length === fileEnvironments.length}
                onClick={(event) => {
                  handleSelectAllCheckBoxChange();
                  event.stopPropagation();
                }}
              >
                Environment
              </Checkbox>
            </div>
          </div>
          <div className=" w-1/2 items-center justify-start px-16 pt-4 font-bold text-oldgray-900">
            Current deployment
          </div>
        </div>

        {fileEnvironments.map((environment) => {
          const alreadyDeployedHere = Boolean(environment.file?.version_id === versionId);
          return (
            <div
              key={environment.id}
              className={classNames("flex border-b border-oldgray-300", alreadyDeployedHere ? "bg-diagonal-lines" : "")}
            >
              <div
                className={classNames(
                  "flew-row flex h-48 w-1/2 cursor-pointer items-center justify-start gap-6  px-16",
                )}
                onClick={() => (alreadyDeployedHere ? null : handleCheckBoxChange(environmentsSelected, environment))}
              >
                <Checkbox
                  disabled={alreadyDeployedHere}
                  className="mb-2 mr-2"
                  name="environment"
                  value={environment.id}
                  checked={alreadyDeployedHere || environmentsSelected.includes(environment)}
                />
                <div className="flex min-w-0 items-center gap-6">
                  <div className="text-text-12-12 truncate font-medium">
                    <EnvironmentTag environment={environment} size={24} />
                  </div>
                  {environment.tag == "default" && <DefaultTag />}
                </div>
              </div>

              <div className="w-1/2">
                <VersionNameCell key={environment.id} file={environment.file} active={alreadyDeployedHere} />
              </div>
            </div>
          );
        })}
      </div>

      {newEnvironments && newEnvironments.length > 0 && (
        <div className="flex items-center gap-12 rounded-ms bg-background-warning-1 px-12 py-12">
          <Warning size={20} className="h-20 w-20 fill-icon-warning-2" />
          <span className="text-13-20 font-normal text-text-warning-2">
            This will change {getJoinedEnvironmentNames(newEnvironments)} to use this version.
          </span>
        </div>
      )}

      <div className="flex flex-row justify-between align-middle">
        <Button
          size={36}
          onClick={async () => {
            onClose();
          }}
        >
          Cancel
        </Button>

        <Button
          size={36}
          styling="solid"
          shade={"black"}
          onClick={onDeploy}
          IconLeft={CheckIcon}
          disabled={environmentsSelected.length === 0 || newEnvironments.length === 0}
        >
          Deploy
        </Button>
      </div>
    </div>
  );
};

export function VersionNameCell({ file, active }: { file: File | null; active: boolean }) {
  return (
    <div
      className={classNames(
        "flew-row flex h-48 w-full items-center justify-start gap-6 px-16",
        active ? " bg-diagonal-lines" : "",
      )}
    >
      {file ? (
        <div>
          <FileLabelWithPopover
            tooltipContentProps={{
              align: "end",
              side: "right",
            }}
            file={file}
            size={24}
            displayFile={false}
          />
        </div>
      ) : (
        <None />
      )}
    </div>
  );
}
